import React, { useState, useEffect, useMemo } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	Alert,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	IconButton,
	Tooltip,
	Link,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BugReportIcon from "@mui/icons-material/BugReport";
import FilterListIcon from "@mui/icons-material/FilterList";
import { apiClient } from "../../services/api";

const StyledDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		borderRadius: "3px",
		maxWidth: "900px",
		width: "100%",
		margin: "16px",
		boxShadow:
			"0 20px 32px -8px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)",
		border: "none",
	},
}));

const getStatusColor = (status) => {
	switch (status?.toLowerCase()) {
		case "open":
		case "to do":
			return "#0052CC";
		case "in progress":
			return "#0747A6";
		case "code review":
			return "#FF8B00";
		case "testing":
			return "#FF5630";
		case "done":
		case "closed":
			return "#00875A";
		default:
			return "#6B778C";
	}
};

const getPriorityColor = (priority) => {
	switch (priority?.toLowerCase()) {
		case "priority 1":
		case "highest":
			return "#CD1316";
		case "priority 2":
		case "high":
			return "#EA7D24";
		case "priority 3":
		case "medium":
			return "#2D8738";
		case "low":
		case "lowest":
			return "#6B778C";
		default:
			return "#6B778C";
	}
};

const formatDate = (dateString) => {
	if (!dateString) return "N/A";
	try {
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	} catch (error) {
		return "Invalid Date";
	}
};

const TicketStatusModal = ({ open, onClose, username }) => {
	const [tickets, setTickets] = useState([]);
	const [loading, setLoading] = useState(false);
	const [refreshing, setRefreshing] = useState(false);
	const [error, setError] = useState("");

	// Filter state
	const [filterType, setFilterType] = useState("");
	const [filterPriority, setFilterPriority] = useState("");
	const [filterStatus, setFilterStatus] = useState("");
	const [filterDateFrom, setFilterDateFrom] = useState("");
	const [filterDateTo, setFilterDateTo] = useState("");

	// Derived unique options from loaded tickets
	const typeOptions = useMemo(
		() => [...new Set(tickets.map((t) => t.issue_type).filter(Boolean))],
		[tickets],
	);
	const priorityOptions = useMemo(
		() => [...new Set(tickets.map((t) => t.priority).filter(Boolean))],
		[tickets],
	);
	const statusOptions = useMemo(
		() => [...new Set(tickets.map((t) => t.status).filter(Boolean))],
		[tickets],
	);

	// Filtered tickets
	const filteredTickets = useMemo(() => {
		return tickets.filter((ticket) => {
			if (filterType && ticket.issue_type !== filterType) return false;
			if (filterPriority && ticket.priority !== filterPriority) return false;
			if (filterStatus && ticket.status !== filterStatus) return false;
			if (filterDateFrom || filterDateTo) {
				const created = ticket.created_at ? new Date(ticket.created_at) : null;
				if (!created) return false;
				if (filterDateFrom && created < new Date(filterDateFrom)) return false;
				if (
					filterDateTo &&
					created > new Date(new Date(filterDateTo).setHours(23, 59, 59, 999))
				)
					return false;
			}
			return true;
		});
	}, [
		tickets,
		filterType,
		filterPriority,
		filterStatus,
		filterDateFrom,
		filterDateTo,
	]);

	const hasActiveFilters =
		filterType ||
		filterPriority ||
		filterStatus ||
		filterDateFrom ||
		filterDateTo;

	const clearFilters = () => {
		setFilterType("");
		setFilterPriority("");
		setFilterStatus("");
		setFilterDateFrom("");
		setFilterDateTo("");
	};

	useEffect(() => {
		if (open && username) {
			fetchUserTickets();
		}
	}, [open, username]);

	const fetchUserTickets = async () => {
		try {
			setLoading(true);
			setError("");

			const response = await apiClient.get(`/api/jira/my-tickets/${username}`);

			if (response.data.success) {
				setTickets(response.data.tickets || []);
			} else {
				setError("Failed to fetch tickets");
			}
		} catch (error) {
			console.error("Error fetching tickets:", error);
			setError("Failed to fetch tickets. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const refreshTicketStatuses = async () => {
		try {
			setRefreshing(true);
			setError("");

			const response = await apiClient.post(
				`/api/jira/refresh-ticket-status/${username}`,
			);

			if (response.data.success) {
				// Refresh the tickets list
				await fetchUserTickets();
			} else {
				setError("Failed to refresh ticket statuses");
			}
		} catch (error) {
			console.error("Error refreshing ticket statuses:", error);
			setError("Failed to refresh ticket statuses. Please try again.");
		} finally {
			setRefreshing(false);
		}
	};
	const handleClose = () => {
		setTickets([]);
		setError("");
		clearFilters();
		onClose();
	};

	return (
		<StyledDialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
			<DialogTitle
				sx={{
					color: "#172B4D",
					fontWeight: 600,
					fontSize: "18px",
					padding: "16px 20px 12px 20px",
					borderBottom: "2px solid #DFE1E6",
					margin: 0,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				{" "}
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					📋 My JIRA Tickets
					{tickets.length > 0 && (
						<Chip
							label={`${filteredTickets.length}${hasActiveFilters ? ` / ${tickets.length}` : ""} ticket${tickets.length !== 1 ? "s" : ""}`}
							size="small"
							sx={{
								backgroundColor: "#E3FCEF",
								color: "#00875A",
								fontSize: "11px",
							}}
						/>
					)}
				</Box>
				{/* <Tooltip title="Refresh ticket statuses">
          <IconButton
            onClick={refreshTicketStatuses}
            disabled={refreshing || loading}
            sx={{ color: '#0052CC' }}
          >
            <RefreshIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Tooltip> */}
			</DialogTitle>

			<DialogContent
				sx={{
					padding: "20px",
					backgroundColor: "#FFFFFF",
					minHeight: "400px",
				}}
			>
				{error && (
					<Alert
						severity="error"
						sx={{
							mb: 2,
							borderRadius: "3px",
							backgroundColor: "#FFEBE6",
							border: "2px solid #DE350B",
							"& .MuiAlert-icon": {
								color: "#DE350B",
							},
						}}
					>
						{error}
					</Alert>
				)}

				{loading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: "300px",
							flexDirection: "column",
							gap: 2,
						}}
					>
						<CircularProgress size={40} sx={{ color: "#cc0000" }} />
						<Typography variant="body2" sx={{ color: "#6B778C" }}>
							Loading your tickets...
						</Typography>
					</Box>
				) : tickets.length === 0 ? (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							minHeight: "300px",
							gap: 2,
						}}
					>
						<AssignmentIcon sx={{ fontSize: 48, color: "#C1C7D0" }} />
						<Typography variant="h6" sx={{ color: "#6B778C" }}>
							No tickets found
						</Typography>
						<Typography
							variant="body2"
							sx={{ color: "#6B778C", textAlign: "center" }}
						>
							You haven't created any JIRA tickets yet. <br />
							Create your first ticket to track your issues!
						</Typography>
					</Box>
				) : (
					<>
						{/* ── Filter Bar ── */}
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								gap: 1.5,
								alignItems: "flex-end",
								mt: 1,
								mb: 2,
								p: 1.5,
								backgroundColor: "#F4F5F7",
								border: "1px solid #DFE1E6",
								borderRadius: "3px",
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.5,
									mr: 0.5,
								}}
							>
								<FilterListIcon sx={{ fontSize: "16px", color: "#5E6C84" }} />
								<Typography
									variant="body2"
									sx={{ fontSize: "12px", fontWeight: 600, color: "#5E6C84" }}
								>
									Filters:
								</Typography>
							</Box>

							{/* Type */}
							<FormControl size="small" sx={{ minWidth: 110 }}>
								<InputLabel sx={{ fontSize: "12px" }}>Type</InputLabel>
								<Select
									value={filterType}
									label="Type"
									onChange={(e) => setFilterType(e.target.value)}
									sx={{
										fontSize: "12px",
										backgroundColor: "white",
										height: "32px",
									}}
								>
									<MenuItem value="">
										<em>All</em>
									</MenuItem>
									{typeOptions.map((t) => (
										<MenuItem key={t} value={t} sx={{ fontSize: "12px" }}>
											<Box
												sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
											>
												{t === "Task" ? (
													<AssignmentIcon
														sx={{ fontSize: "14px", color: "#0052CC" }}
													/>
												) : (
													<BugReportIcon
														sx={{ fontSize: "14px", color: "#DE350B" }}
													/>
												)}
												{t}
											</Box>
										</MenuItem>
									))}
								</Select>
							</FormControl>

							{/* Priority */}
							<FormControl size="small" sx={{ minWidth: 120 }}>
								<InputLabel sx={{ fontSize: "12px" }}>Priority</InputLabel>
								<Select
									value={filterPriority}
									label="Priority"
									onChange={(e) => setFilterPriority(e.target.value)}
									sx={{
										fontSize: "12px",
										backgroundColor: "white",
										height: "32px",
									}}
								>
									<MenuItem value="">
										<em>All</em>
									</MenuItem>
									{priorityOptions.map((p) => (
										<MenuItem key={p} value={p} sx={{ fontSize: "12px" }}>
											<Box
												sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
											>
												<Box
													sx={{
														width: 8,
														height: 8,
														borderRadius: "50%",
														backgroundColor: getPriorityColor(p),
														flexShrink: 0,
													}}
												/>
												{p}
											</Box>
										</MenuItem>
									))}
								</Select>
							</FormControl>

							{/* Status */}
							<FormControl size="small" sx={{ minWidth: 120 }}>
								<InputLabel sx={{ fontSize: "12px" }}>Status</InputLabel>
								<Select
									value={filterStatus}
									label="Status"
									onChange={(e) => setFilterStatus(e.target.value)}
									sx={{
										fontSize: "12px",
										backgroundColor: "white",
										height: "32px",
									}}
								>
									<MenuItem value="">
										<em>All</em>
									</MenuItem>
									{statusOptions.map((s) => (
										<MenuItem key={s} value={s} sx={{ fontSize: "12px" }}>
											<Box
												sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
											>
												<Box
													sx={{
														width: 8,
														height: 8,
														borderRadius: "50%",
														backgroundColor: getStatusColor(s),
														flexShrink: 0,
													}}
												/>
												{s}
											</Box>
										</MenuItem>
									))}
								</Select>
							</FormControl>

							{/* Created: date range */}
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
								<Typography
									variant="body2"
									sx={{
										fontSize: "11px",
										color: "#5E6C84",
										whiteSpace: "nowrap",
									}}
								>
									Created:
								</Typography>
								<TextField
									type="date"
									size="small"
									value={filterDateFrom}
									onChange={(e) => setFilterDateFrom(e.target.value)}
									InputLabelProps={{ shrink: true }}
									inputProps={{
										style: { fontSize: "12px", padding: "5px 8px" },
									}}
									sx={{ width: "135px", backgroundColor: "white" }}
									placeholder="From"
								/>
								<Typography
									variant="body2"
									sx={{ fontSize: "11px", color: "#5E6C84" }}
								>
									–
								</Typography>
								<TextField
									type="date"
									size="small"
									value={filterDateTo}
									onChange={(e) => setFilterDateTo(e.target.value)}
									InputLabelProps={{ shrink: true }}
									inputProps={{
										style: { fontSize: "12px", padding: "5px 8px" },
									}}
									sx={{ width: "135px", backgroundColor: "white" }}
									placeholder="To"
								/>
							</Box>

							{/* Clear filters */}
							{hasActiveFilters && (
								<Button
									size="small"
									onClick={clearFilters}
									sx={{
										textTransform: "none",
										fontSize: "12px",
										color: "#DE350B",
										height: "32px",
										"&:hover": { backgroundColor: "#FFEBE6" },
									}}
								>
									Clear
								</Button>
							)}
						</Box>

						{/* ── Tickets Table ── */}
						<TableContainer
							component={Paper}
							sx={{ boxShadow: "none", border: "1px solid #DFE1E6" }}
						>
							<Table size="small">
								<TableHead>
									<TableRow sx={{ backgroundColor: "#F4F5F7" }}>
										<TableCell
											sx={{
												fontWeight: 600,
												fontSize: "12px",
												color: "#5E6C84",
											}}
										>
											Ticket
										</TableCell>
										<TableCell
											sx={{
												fontWeight: 600,
												fontSize: "12px",
												color: "#5E6C84",
											}}
										>
											Summary
										</TableCell>
										<TableCell
											sx={{
												fontWeight: 600,
												fontSize: "12px",
												color: "#5E6C84",
											}}
										>
											Type
										</TableCell>
										<TableCell
											sx={{
												fontWeight: 600,
												fontSize: "12px",
												color: "#5E6C84",
											}}
										>
											Priority
										</TableCell>
										<TableCell
											sx={{
												fontWeight: 600,
												fontSize: "12px",
												color: "#5E6C84",
											}}
										>
											Status
										</TableCell>
										<TableCell
											sx={{
												fontWeight: 600,
												fontSize: "12px",
												color: "#5E6C84",
											}}
										>
											Created
										</TableCell>
										<TableCell
											sx={{
												fontWeight: 600,
												fontSize: "12px",
												color: "#5E6C84",
											}}
										>
											Actions
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{filteredTickets.length === 0 ? (
										<TableRow>
											<TableCell
												colSpan={7}
												sx={{
													textAlign: "center",
													py: 4,
													color: "#6B778C",
													fontSize: "13px",
												}}
											>
												No tickets match the selected filters.
											</TableCell>
										</TableRow>
									) : (
										filteredTickets.map((ticket, index) => (
											<TableRow
												key={index}
												sx={{
													"&:hover": { backgroundColor: "#F4F5F7" },
													borderBottom: "1px solid #DFE1E6",
												}}
											>
												<TableCell sx={{ fontSize: "13px", fontWeight: 500 }}>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 1,
														}}
													>
														<Typography
															variant="body2"
															sx={{ fontWeight: 600, color: "#0052CC" }}
														>
															{ticket.ticket_key}
														</Typography>
													</Box>
												</TableCell>
												<TableCell sx={{ fontSize: "13px", maxWidth: "200px" }}>
													<Typography
														variant="body2"
														sx={{
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}
														title={ticket.summary}
													>
														{ticket.summary}
													</Typography>
												</TableCell>
												<TableCell sx={{ fontSize: "13px" }}>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 0.5,
														}}
													>
														{ticket.issue_type === "Task" ? (
															<AssignmentIcon
																sx={{ fontSize: "14px", color: "#0052CC" }}
															/>
														) : (
															<BugReportIcon
																sx={{ fontSize: "14px", color: "#DE350B" }}
															/>
														)}
														<Typography
															variant="body2"
															sx={{ fontSize: "12px" }}
														>
															{ticket.issue_type}
														</Typography>
													</Box>
												</TableCell>
												<TableCell sx={{ fontSize: "13px" }}>
													<Chip
														label={ticket.priority}
														size="small"
														sx={{
															backgroundColor: `${getPriorityColor(ticket.priority)}20`,
															color: getPriorityColor(ticket.priority),
															fontSize: "10px",
															height: "18px",
															borderRadius: "3px",
														}}
													/>
												</TableCell>
												<TableCell sx={{ fontSize: "13px" }}>
													<Chip
														label={ticket.status}
														size="small"
														sx={{
															backgroundColor: `${getStatusColor(ticket.status)}20`,
															color: getStatusColor(ticket.status),
															fontSize: "10px",
															height: "18px",
															borderRadius: "3px",
														}}
													/>
												</TableCell>
												<TableCell sx={{ fontSize: "13px", color: "#6B778C" }}>
													{formatDate(ticket.created_at)}
												</TableCell>
												<TableCell sx={{ fontSize: "13px" }}>
													<Tooltip title="Open in JIRA">
														<IconButton
															size="small"
															onClick={() =>
																window.open(ticket.jira_url, "_blank")
															}
															sx={{ color: "#0052CC" }}
														>
															<OpenInNewIcon sx={{ fontSize: "14px" }} />
														</IconButton>
													</Tooltip>
												</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				)}

				{refreshing && (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							mt: 2,
							gap: 1,
						}}
					>
						<CircularProgress size={16} sx={{ color: "#0052CC" }} />
						<Typography variant="body2" sx={{ color: "#6B778C" }}>
							Refreshing ticket statuses...
						</Typography>
					</Box>
				)}
			</DialogContent>

			<DialogActions
				sx={{
					padding: "12px 20px",
					gap: 1,
					justifyContent: "flex-end",
					borderTop: "2px solid #DFE1E6",
					backgroundColor: "#FAFBFC",
				}}
			>
				<Button
					onClick={handleClose}
					variant="text"
					sx={{
						textTransform: "none",
						color: "#5E6C84",
						fontSize: "14px",
						minWidth: "80px",
						height: "32px",
						fontWeight: 500,
						"&:hover": {
							backgroundColor: "#F4F5F7",
						},
					}}
				>
					Close
				</Button>
			</DialogActions>
		</StyledDialog>
	);
};

export default TicketStatusModal;
